import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, FileSpreadsheet, Crown, CreditCard, Store, Mail, ShieldCheck, HeartPulse, ClipboardCheck, Handshake,
} from 'lucide-react';

interface AdminNavigationProps {
  currentPath: string;
}

interface NavItem { path: string; icon: React.ElementType; label: string }
interface NavSection { label: string; items: NavItem[] }

// Reorden del admin (24-oct-2026, mismo principio que la F4 del panel del dueño): grupos por la pregunta que
// responden. Medido en GA4 a 90 días: «Usuarios y Clientes» era el 89 % del uso (buscar negocio → entrar como);
// 13 entradas sumaban 30 vistas. Fuera del menú (rutas vivas): Notificaciones (0 creadas desde junio),
// Configurar Pagos (→ pestaña Ajustes de Pagos), Solicitudes correo (→ pestaña de Correos) y Analíticas
// Globales (sus paneles reales viven en el Inicio; su «Estado del Sistema» era Firebase cableado en duro).
const SECTIONS: NavSection[] = [
  { label: 'Inicio', items: [{ path: '/dashboard', icon: Home, label: 'Inicio' }] },
  {
    label: 'Negocios',
    items: [
      { path: '/dashboard/admin/users', icon: Store, label: 'Negocios' },
      { path: '/dashboard/admin/subscriptions', icon: Crown, label: 'Suscripciones' },
      { path: '/dashboard/admin/adopcion', icon: ClipboardCheck, label: 'Adopción' },
    ],
  },
  {
    label: 'Cobros',
    items: [
      { path: '/dashboard/admin/payments', icon: CreditCard, label: 'Pagos' },
      { path: '/dashboard/admin/referrals', icon: Handshake, label: 'Referidos' },
    ],
  },
  { label: 'Correos', items: [{ path: '/dashboard/admin/emails', icon: Mail, label: 'Correos' }] },
  {
    // Lo que el vigía ya avisa por WhatsApp; se abre para ver el detalle de una alerta.
    label: 'Salud',
    items: [
      { path: '/dashboard/admin/wa-health', icon: HeartPulse, label: 'Salud WhatsApp' },
      { path: '/dashboard/admin/audits', icon: ShieldCheck, label: 'Auditorías' },
    ],
  },
  { label: 'Herramientas', items: [{ path: '/dashboard/admin/import', icon: FileSpreadsheet, label: 'Importar clientes' }] },
];

// La ficha de un negocio (/admin/negocios/:id) y Módulos (/admin/tenants/:id/modules) cuelgan de «Negocios».
const activoDe = (currentPath: string, path: string): boolean => {
  if (currentPath === path) return true;
  if (path === '/dashboard/admin/users') return /^\/dashboard\/admin\/(negocios|tenants)\//.test(currentPath);
  return false;
};

const AdminNavigation: React.FC<AdminNavigationProps> = ({ currentPath }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-1">
      {SECTIONS.map((section, i) => (
        <div key={section.label}>
          {i > 0 && (
            <div className="pt-3 pb-1 px-4">
              <p className="text-[10px] font-semibold text-content-3 uppercase tracking-wider" data-testid="nav-grupo">{section.label}</p>
            </div>
          )}
          {section.items.map((item) => {
            const on = activoDe(currentPath, item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid="nav-item"
                data-path={item.path}
                data-grupo={section.label}
                className="w-full group flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ease-in-out"
                style={{
                  // Accent fijo Lealify (mode-aware vía token), no color de marca
                  backgroundColor: on ? 'rgb(var(--c-accent) / 0.16)' : '',
                  color: on ? 'rgb(var(--c-accent))' : 'rgb(var(--c-content))',
                }}
              >
                <item.icon className="mr-3 h-5 w-5" style={{ color: on ? 'rgb(var(--c-accent))' : 'rgb(var(--c-content-2))' }} />
                <span className="transition-colors duration-200 ease-in-out">{item.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default AdminNavigation;
